import { useEffect, useState } from 'react';
import { DataTable, type Column } from '../components/data-table';
import { IvrEditor } from './IvrEditor';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Table,
  Td,
  Select,
  Th,
} from '../components/ui';
import { DestinationPicker, type DestinationValue } from '../components/DestinationPicker';
import { api, type Extension } from '../lib/api';

const HANGUP: DestinationValue = {
  destinationType: 'hangup',
  destinationId: null,
  destinationValue: null,
};

const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'] as const;

export function CallFlows() {
  const [tab, setTab] = useState<'ivr' | 'ring' | 'queue'>('ivr');

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-lg font-semibold">Call flows</h1>
        <p className="text-sm text-muted-foreground">
          Menus, groups and queues that decide where a call goes.
        </p>
      </header>

      <nav className="flex gap-1 border-b border-border">
        {(
          [
            ['ivr', 'IVR menus'],
            ['ring', 'Ring groups'],
            ['queue', 'Queues'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={
              tab === key
                ? '-mb-px border-b-2 border-primary px-3 py-2 text-sm font-medium'
                : 'px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
            }
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === 'ivr' ? <Ivrs /> : <MemberGroups kind={tab} />}
    </div>
  );
}

// -------------------------------------------------------------------------------------------

interface IvrRow {
  id: string;
  name: string;
  number: string | null;
}

function Ivrs() {
  const queryClient = useQueryClient();
  /** null = list; 'new' = create; otherwise the id being edited. */
  const [editing, setEditing] = useState<string | null>(null);

  const { data: ivrs = [] } = useQuery({
    queryKey: ['ivrs'],
    queryFn: () => api.get<IvrRow[]>('/api/v1/ivrs'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/ivrs/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ivrs'] });
      void queryClient.invalidateQueries({ queryKey: ['destinations'] });
    },
  });

  if (editing !== null) {
    return <IvrEditor id={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />;
  }

  const columns: Array<Column<IvrRow>> = [
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (i) => <span className="font-medium">{i.name}</span>,
    },
    {
      key: 'number',
      header: 'Direct number',
      render: (i) =>
        i.number ? <code>{i.number}</code> : <span className="text-muted-foreground">—</span>,
    },
  ];

  return (
    <DataTable
      rows={ivrs}
      columns={columns}
      rowKey={(i) => i.id}
      search={(i) => `${i.name} ${i.number ?? ''}`}
      searchPlaceholder="Search menus"
      toolbar={<Button onClick={() => setEditing('new')}>New IVR menu</Button>}
      onEdit={(row) => setEditing(row.id)}
      onDelete={(rows) => rows.forEach((r) => remove.mutate(r.id))}
      deleteLabel={(rows) =>
        `Delete ${rows.length} menu(s)? Anything routing to them will need a new destination.`
      }
      empty="No IVR menus. A menu lets callers choose where to go."
    />
  );
}

/**
 * Ring groups and queues.
 *
 * One component for both. They differ in strategy vocabulary and in what happens when nobody
 * answers, but both are "a name, a number, some members and a fallback" — and both were
 * read-only tables until now: create, then never correct. A group with the wrong members had no
 * fix, because delete was missing too.
 */
function MemberGroups({ kind }: { kind: 'ring' | 'queue' }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);

  const path = kind === 'ring' ? '/api/v1/ring-groups' : '/api/v1/queues';
  const key = kind === 'ring' ? 'ring-groups' : 'queues';
  const noun = kind === 'ring' ? 'ring group' : 'queue';

  const { data: rows = [], isLoading } = useQuery({
    queryKey: [key],
    queryFn: () =>
      api.get<Array<{ id: string; name: string; number: string; strategy: string }>>(path),
  });

  // Membership is not in the list projection — twenty queues do not need two hundred rows of it —
  // so the counts shown per row come from one extra request rather than one per queue.
  const { data: extensions = [] } = useQuery({
    queryKey: ['extensions'],
    queryFn: () => api.get<Extension[]>('/api/v1/extensions'),
  });

  const { data: membership = {} } = useQuery({
    queryKey: [key, 'membership', rows.map((r) => r.id).join(',')],
    enabled: rows.length > 0,
    queryFn: async () => {
      const out: Record<string, { agents: string[]; dynamic: string[]; managers: string[] }> = {};
      for (const row of rows) {
        const detail = await api.get<{
          members?: Array<{ extensionId: string; isDynamic?: boolean }>;
          memberExtensionIds?: string[];
          managerExtensionIds?: string[];
        }>(`${path}/${row.id}`);

        const numberOf = (id: string) => extensions.find((e) => e.id === id)?.number ?? '';

        const members = detail.members ?? [];
        out[row.id] = {
          agents: (
            detail.memberExtensionIds ??
            members.filter((m) => !m.isDynamic).map((m) => m.extensionId)
          )
            .map(numberOf)
            .filter(Boolean),
          dynamic: members
            .filter((m) => m.isDynamic)
            .map((m) => numberOf(m.extensionId))
            .filter(Boolean),
          managers: (detail.managerExtensionIds ?? []).map(numberOf).filter(Boolean),
        };
      }
      return out;
    },
  });

  /** A short list of numbers, with the rest folded into a count. */
  const Chips = ({ numbers }: { numbers: string[] }) => {
    if (numbers.length === 0) return <span className="text-muted-foreground">—</span>;
    return (
      <span className="flex flex-wrap gap-1">
        {numbers.slice(0, 3).map((n) => (
          <Badge key={n}>{n}</Badge>
        ))}
        {numbers.length > 3 && (
          <span className="text-xs text-muted-foreground">+{numbers.length - 3}</span>
        )}
      </span>
    );
  };

  const remove = useMutation({
    mutationFn: async (targets: ReadonlyArray<{ id: string }>) => {
      for (const t of targets) await api.del(`${path}/${t.id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [key] });
      void queryClient.invalidateQueries({ queryKey: ['destinations'] });
    },
  });

  if (editing !== null) {
    return (
      <MemberGroupEditor
        kind={kind}
        id={editing === 'new' ? null : editing}
        onClose={() => {
          setEditing(null);
          void queryClient.invalidateQueries({ queryKey: [key] });
          void queryClient.invalidateQueries({ queryKey: ['destinations'] });
        }}
      />
    );
  }

  const columns: ReadonlyArray<
    Column<{ id: string; name: string; number: string; strategy: string }>
  > = [
    {
      key: 'number',
      header: 'Number',
      width: '8rem',
      sort: (a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }),
      render: (r) => <span className="font-mono">{r.number}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (r) => r.name,
    },
    { key: 'strategy', header: 'Strategy', render: (r) => <Badge>{r.strategy}</Badge> },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(r) => r.id}
      search={(r) => `${r.number} ${r.name} ${r.strategy}`}
      searchPlaceholder={`Search ${noun}s`}
      toolbar={<Button onClick={() => setEditing('new')}>New {noun}</Button>}
      onEdit={(r) => setEditing(r.id)}
      onDelete={(targets) => remove.mutate(targets)}
      deleteLabel={(targets) =>
        targets.length === 1
          ? `Delete ${noun} ${targets[0]!.number}? Anything routing to it will stop working.`
          : `Delete ${targets.length} ${noun}s? Anything routing to them will stop working.`
      }
      empty={
        isLoading ? null : (
          <div className="py-10 text-center">
            <p className="font-medium">No {noun}s</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {kind === 'ring'
                ? 'A ring group rings several people at once.'
                : 'A queue holds callers until an agent is free.'}
            </p>
          </div>
        )
      }
    />
  );
}

/**
 * Shared form for ring groups and queues.
 *
 * They differ in strategy vocabulary and in what happens when nobody answers, but both are "a
 * name, a number, some members and a fallback" — duplicating the form would mean fixing every
 * layout bug twice.
 */
function MemberGroupEditor({
  kind,
  id,
  onClose,
}: {
  kind: 'ring' | 'queue';
  id: string | null;
  onClose: () => void;
}) {
  const creating = id === null;
  const path = kind === 'ring' ? '/api/v1/ring-groups' : '/api/v1/queues';

  const [name, setName] = useState('');
  const [number, setNumber] = useState('');
  const [strategy, setStrategy] = useState(kind === 'ring' ? 'ringall' : 'rrmemory');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  /** Agents who log themselves in and out, rather than being permanently in the queue. */
  const [dynamicIds, setDynamicIds] = useState<string[]>([]);
  const [managerIds, setManagerIds] = useState<string[]>([]);
  const [fallback, setFallback] = useState<DestinationValue>(HANGUP);

  const { data: extensions = [] } = useQuery({
    queryKey: ['extensions'],
    queryFn: () => api.get<Extension[]>('/api/v1/extensions'),
  });

  const { data: existing } = useQuery({
    queryKey: [kind === 'ring' ? 'ring-group' : 'queue', id],
    queryFn: () => api.get<Record<string, unknown>>(`${path}/${id}`),
    enabled: !creating,
  });

  useEffect(() => {
    if (!existing) return;
    setName(String(existing['name'] ?? ''));
    setNumber(String(existing['number'] ?? ''));
    setStrategy(String(existing['strategy'] ?? ''));
    // Members arrive in their stored order, which is the hunt order — preserving it matters,
    // because saving a reshuffled list would quietly change who rings first.
    const members =
      (existing['members'] as Array<{ extensionId: string; isDynamic?: boolean }> | undefined) ??
      [];

    setMemberIds(
      kind === 'ring'
        ? ((existing['memberExtensionIds'] as string[] | undefined) ?? [])
        : members.filter((m) => !m.isDynamic).map((m) => m.extensionId),
    );
    setDynamicIds(members.filter((m) => m.isDynamic).map((m) => m.extensionId));
    setManagerIds((existing['managerExtensionIds'] as string[] | undefined) ?? []);
    const prefix = kind === 'ring' ? 'noanswer' : 'failover';
    setFallback({
      destinationType: String(existing[`${prefix}DestinationType`] ?? 'hangup'),
      destinationId: (existing[`${prefix}DestinationId`] as string | null) ?? null,
      destinationValue: (existing[`${prefix}DestinationValue`] as string | null) ?? null,
    });
  }, [existing, kind]);

  const body = () =>
    kind === 'ring'
      ? { name, number, strategy, memberExtensionIds: memberIds, noanswer: fallback }
      : {
          name,
          number,
          strategy,
          // Penalty is the position in the list, so reordering the selection reorders who is
          // tried first — the same meaning `seq` carries for a ring group.
          members: [
            ...memberIds.map((extensionId, penalty) => ({
              extensionId,
              penalty,
              isDynamic: false,
            })),
            ...dynamicIds.map((extensionId, penalty) => ({
              extensionId,
              penalty: memberIds.length + penalty,
              isDynamic: true,
            })),
          ],
          managerExtensionIds: managerIds,
          failover: fallback,
        };

  const create = useMutation({
    mutationFn: () => (creating ? api.post(path, body()) : api.put(`${path}/${id}`, body())),
    onSuccess: onClose,
  });

  const strategies =
    kind === 'ring'
      ? ['ringall', 'sequential', 'random', 'memoryhunt']
      : ['ringall', 'rrmemory', 'leastrecent', 'fewestcalls', 'linear', 'random'];

  return (
    <Card className="space-y-4">
      <h2 className="font-medium">
        {creating ? 'New' : 'Edit'} {kind === 'ring' ? 'ring group' : 'queue'}
      </h2>

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label="Number">
            <Input
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder={kind === 'ring' ? '600' : '700'}
              required
            />
          </Field>
          <Field label="Strategy">
            <Select value={strategy} onChange={setStrategy}>
              {strategies.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label={kind === 'queue' ? 'Static agents' : 'Members'}>
          <div className="space-y-1.5">
            {extensions.map((ext) => (
              <label key={ext.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={memberIds.includes(ext.id)}
                  onChange={(e) =>
                    setMemberIds(
                      e.target.checked
                        ? [...memberIds, ext.id]
                        : memberIds.filter((id) => id !== ext.id),
                    )
                  }
                />
                <span className="font-mono">{ext.number}</span> {ext.name}
                {kind === 'ring' && memberIds.indexOf(ext.id) >= 0 ? (
                  <Badge>#{memberIds.indexOf(ext.id) + 1}</Badge>
                ) : null}
              </label>
            ))}
          </div>
        </Field>

        {kind === 'queue' && (
          <>
            <Field
              label="Dynamic agents"
              hint="They log in and out with a feature code or the API, rather than always being in the queue."
            >
              <div className="space-y-1.5">
                {extensions.map((ext) => (
                  <label key={ext.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={dynamicIds.includes(ext.id)}
                      onChange={(e) =>
                        setDynamicIds(
                          e.target.checked
                            ? [...dynamicIds, ext.id]
                            : dynamicIds.filter((id) => id !== ext.id),
                        )
                      }
                    />
                    <span className="font-mono">{ext.number}</span> {ext.name}
                  </label>
                ))}
              </div>
            </Field>

            <Field
              label="Queue managers"
              hint="They supervise the queue and are never rung by it. A supervisor counted as an agent makes every 'agents available' figure wrong."
            >
              <div className="space-y-1.5">
                {extensions.map((ext) => (
                  <label key={ext.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={managerIds.includes(ext.id)}
                      onChange={(e) =>
                        setManagerIds(
                          e.target.checked
                            ? [...managerIds, ext.id]
                            : managerIds.filter((id) => id !== ext.id),
                        )
                      }
                    />
                    <span className="font-mono">{ext.number}</span> {ext.name}
                  </label>
                ))}
              </div>
            </Field>
          </>
        )}

        <DestinationPicker
          label={
            kind === 'ring' ? 'If nobody answers' : 'If the caller gives up or the queue is full'
          }
          value={fallback}
          onChange={setFallback}
        />

        {create.error ? <Alert>{(create.error as Error).message}</Alert> : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : creating ? 'Create' : 'Save'}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
